import React, { useState, useEffect } from 'react';
import { getAvailableSlots } from '../api/bookingApi';
import { formatDate, getDateRange, formatTime, formatDisplayDate, combineDateAndTime } from '../utils/dateHelpers';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './TimeSlotBubbles.css';

const LOOKAHEAD_DAYS = 10;

function TimeSlotBubbles({ location, onSelectSlot }) {
    const [selectedDate, setSelectedDate] = useState(null);
    const [availableSlots, setAvailableSlots] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Fetch available slots when location changes
    useEffect(() => {
        if (location) {
            fetchAvailableSlots();
        }
    }, [location]);

    const fetchAvailableSlots = async () => {
        setLoading(true);
        setError('');

        try {
            const today = new Date();
            // Calculate end date as 10 calendar days from today
            const end = new Date(today);
            end.setDate(today.getDate() + LOOKAHEAD_DAYS);

            const startDate = formatDate(today);
            const endDate = formatDate(end);
            const bookingThreshold = new Date(today.getTime() + 48 * 60 * 60 * 1000); // Now + 48 hours

            const data = await getAvailableSlots(location, startDate, endDate);

            // Sort and filter slots for each date
            const slots = data.slots || {};
            const filteredSlots = {};

            Object.keys(slots).forEach(date => {
                const daySlots = slots[date];
                const validSlots = daySlots.filter(slot => {
                    const time = typeof slot === 'object' ? slot.time : slot;
                    const [y, m, d] = date.split('-').map(Number);
                    const [hours, minutes] = time.split(':').map(Number);
                    const slotDateTime = new Date(y, m - 1, d, hours, minutes);
                    return slotDateTime >= bookingThreshold;
                });

                if (validSlots.length > 0) {
                    validSlots.sort((a, b) => {
                        const t1 = typeof a === 'object' ? a.time : a;
                        const t2 = typeof b === 'object' ? b.time : b;
                        const [h1, m1] = t1.split(':').map(Number);
                        const [h2, m2] = t2.split(':').map(Number);
                        return h1 - h2 || m1 - m2;
                    });
                    filteredSlots[date] = validSlots;
                }
            });

            setAvailableSlots(filteredSlots);
        } catch (err) {
            setError('Failed to load available slots. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Check if a date has available slots
    const hasSlots = (date) => {
        const dateKey = formatDate(date);
        return availableSlots[dateKey] && availableSlots[dateKey].length > 0;
    };

    // Disable dates without slots or past dates or dates beyond lookahead window
    const tileDisabled = ({ date, view }) => {
        if (view === 'month') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const max = new Date(today);
            max.setDate(today.getDate() + LOOKAHEAD_DAYS);

            if (date < today || date > max) {
                return true; // Disable past dates and dates beyond lookahead window
            }

            return !hasSlots(date);
        }
        return false;
    };

    // Add custom class for dates with availability
    const tileClassName = ({ date, view }) => {
        if (view === 'month' && hasSlots(date)) {
            return 'has-availability';
        }
        return '';
    };

    // M2: Screen-reader-only label on available dates so screen readers announce availability
    const tileContent = ({ date, view }) => {
        if (view === 'month' && hasSlots(date)) {
            return <span className="sr-only">Available</span>;
        }
        return null;
    };

    const handleDateClick = (date) => {
        setSelectedDate(date);
    };

    const handleSlotClick = (slot) => {
        // Handle both string slots (legacy) and object slots (new)
        const time = typeof slot === 'object' ? slot.time : slot;
        const providerId = typeof slot === 'object' ? slot.providerId : null;
        const providerEmail = typeof slot === 'object' ? slot.providerEmail : null;
        const watsSlotId = typeof slot === 'object' ? slot.watsSlotId : null;

        // Ensure we pass the original external start/end times if available
        const slotStart = typeof slot === 'object' ? slot.slotStart : null;
        const slotEnd = typeof slot === 'object' ? slot.slotEnd : null;

        onSelectSlot(selectedDate, time, providerId, providerEmail, watsSlotId, slotStart, slotEnd);
    };

    const selectedDateKey = selectedDate ? formatDate(selectedDate) : null;
    const timeSlotsForDate = selectedDateKey ? (availableSlots[selectedDateKey] || []) : [];

    const todayForMax = new Date();
    todayForMax.setHours(0, 0, 0, 0);
    const maxCalendarDate = new Date(todayForMax);
    maxCalendarDate.setDate(maxCalendarDate.getDate() + LOOKAHEAD_DAYS);

    return (
        <div className="time-slot-bubbles">
            {/* H3: role=status for polite loading announcement */}
            {loading && (
                <div className="loading" role="status" aria-live="polite">
                    Loading available slots...
                </div>
            )}
            {/* H3: role=alert for immediate error announcement */}
            {error && (
                <div className="error" role="alert">
                    {error}
                </div>
            )}

            {!loading && !error && (
                <>
                    <div className="calendar-container">
                        <h3>Select a Date</h3>
                        <Calendar
                            onChange={handleDateClick}
                            value={selectedDate}
                            tileDisabled={tileDisabled}
                            tileClassName={tileClassName}
                            tileContent={tileContent}
                            minDate={new Date()}
                            maxDate={maxCalendarDate}
                        />
                    </div>

                    {selectedDate && (
                        <div className="time-slots-container">
                            <h3>Available Times for {formatDisplayDate(selectedDate)}</h3>
                            {timeSlotsForDate.length === 0 ? (
                                <p className="no-slots">No available slots for this date</p>
                            ) : (
                                <div className="time-slots-grid">
                                    {(() => {
                                        // Group slots by time to deduplicate
                                        const uniqueTimesMap = new Map();

                                        [...timeSlotsForDate]
                                            .sort((a, b) => {
                                                const t1 = typeof a === 'object' ? a.time : a;
                                                const t2 = typeof b === 'object' ? b.time : b;
                                                const [h1, m1] = t1.split(':').map(Number);
                                                const [h2, m2] = t2.split(':').map(Number);
                                                return h1 - h2 || m1 - m2;
                                            })
                                            .forEach(slot => {
                                                const time = typeof slot === 'object' ? slot.time : slot;
                                                if (!uniqueTimesMap.has(time)) {
                                                    uniqueTimesMap.set(time, slot);
                                                }
                                            });

                                        return Array.from(uniqueTimesMap.values()).map((slot, index) => {
                                            const time = typeof slot === 'object' ? slot.time : slot;
                                            return (
                                                <button
                                                    key={`${time}-${index}`}
                                                    className="time-slot-bubble"
                                                    onClick={() => handleSlotClick(slot)}
                                                >
                                                    {formatTime(time)}
                                                </button>
                                            );
                                        });
                                    })()}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default TimeSlotBubbles;
